import React, { useEffect, useState } from 'react';
import IconoEstadoCita from './IconoEstadoCita';
import { getCitas } from '../../services/citaService';
import { getMonday, addDays } from '../../utils/fechas';
import {
  HORA_INICIO, HORA_FIN, PX_HORA, ALTURA_MINIMA_CITA, mismaFecha, citasDelDia,
  distribuirCitas, posicionCita, horaTexto, colorFondoCita, colorBordeCita
} from '../../utils/distribucionCitas';
import '../../styles/CalendarioDistribucion.css';

const HORAS = Array.from({ length: HORA_FIN - HORA_INICIO }, (_, i) => HORA_INICIO + i);
const MAX_VISIBLES = 2; // Cuando hay más de 3 simultáneas: 2 tarjetas + 1 indicador.

export default function VistaSemanal({ fecha, profesional, refrescar, onSelectCita, onNuevaCita }) {
  const [citas, setCitas] = useState([]);
  const [grupoAbierto, setGrupoAbierto] = useState(null);
  const inicioSemana = getMonday(fecha);
  const dias = Array.from({ length: 5 }, (_, i) => addDays(inicioSemana, i));

  useEffect(() => {
    let activo = true;
    getCitas({
      profesional,
      from: inicioSemana.toISOString().slice(0, 10),
      to: addDays(inicioSemana, 4).toISOString().slice(0, 10)
    }).then(datos => { if (activo) setCitas(datos); })
      .catch(err => console.error('Error cargando citas:', err));
    return () => { activo = false; };
  }, [fecha, profesional, refrescar]);

  useEffect(() => { setGrupoAbierto(null); }, [fecha, profesional, refrescar]);

  const hoy = new Date();
  const topHoraActual = ((hoy.getHours() - HORA_INICIO) * 60 + hoy.getMinutes()) * PX_HORA / 60;
  const citasFiltradas = citas.filter(c => profesional === 'ALL' || c.profesional === profesional);

  function dibujarCita(item, columnas) {
    const { cita } = item;
    const height = Math.max(ALTURA_MINIMA_CITA, (item.end - item.start) * PX_HORA / 60);
    return (
      <button
        key={cita.id}
        type="button"
        className="cita cita-semanal cita-cal-button"
        data-tooltip={`${cita.paciente || ''}\n${cita.tratamiento || ''}`}
        title={`${cita.paciente || ''} — ${cita.tratamiento || ''}`}
        style={{
          ...posicionCita(item, columnas, true),
          borderLeft: `4px solid ${colorBordeCita(cita)}`,
          background: colorFondoCita(cita)
        }}
        onClick={() => onSelectCita(cita)}
      >
        <span className="cita-contenido">
          <strong className="cita-nombre"><IconoEstadoCita estado={cita.estadoAviso} /><span className="cita-nombre-texto">{cita.paciente}</span></strong>
          {height >= 34 && <span className="cita-tratamiento">{cita.tratamiento}</span>}
        </span>
      </button>
    );
  }

  function pintarCitasDia(fechaDia) {
    const grupos = distribuirCitas(citasDelDia(citasFiltradas, fechaDia));
    return grupos.flatMap((grupo, indiceGrupo) => {
      // Sin saturación: cada cita real, columnas reutilizadas y anchura expandible.
      if (grupo.columnas <= 3) return grupo.eventos.map(item => dibujarCita(item, grupo.columnas));

      // Saturación: dos carriles de citas y uno de resumen. Reservar el tercero
      // evita superposiciones incluso si las citas empiezan a distinta hora.
      const visibles = grupo.eventos.filter(item => item.col < MAX_VISIBLES)
        .map(item => ({ ...item, span: 1 }));
      const ocultas = grupo.eventos.filter(item => item.col >= MAX_VISIBLES);
      const alturaGrupo = (grupo.finVisual - grupo.inicio) * PX_HORA / 60;
      const topGrupo = (grupo.inicio - HORA_INICIO * 60) * PX_HORA / 60;
      return [
        ...visibles.map(item => dibujarCita(item, 3)),
        <button
          key={`mas-${indiceGrupo}`}
          type="button"
          className="cita-mas"
          title={`Ver las ${grupo.eventos.length} citas del grupo`}
          style={{
            top: topGrupo,
            height: Math.max(1, alturaGrupo),
            left: 'calc(66.6667% + 2px)',
            width: 'calc(33.3333% - 4px)'
          }}
          onClick={() => setGrupoAbierto({
            fecha: fechaDia,
            eventos: grupo.eventos,
            ocultas: ocultas.length,
            inicio: grupo.inicio,
            fin: grupo.fin
          })}
        >
          <strong>+{ocultas.length}</strong>
          <span>Ver todas</span>
        </button>
      ];
    });
  }

  return (
    <>
      <div className="agenda">
        <div className="agenda-grid">
          <div className="agenda-header">
            <div className="hora-col-header" />
            {dias.map(d => (
              <div key={d.toDateString()} className={`dia-header ${mismaFecha(d, hoy) ? 'hoy' : ''}`}>
                {d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' })}
              </div>
            ))}
          </div>
          <div className="agenda-body">
            <div className="hora-col">
              {HORAS.map(h => <div key={h} className="hora-label">{h}:00</div>)}
            </div>
            <div className="dias-grid">
              {dias.map((diaFecha, col) => (
                <div key={col} className="dia-col">
                  {HORAS.map(h => (
                    <div key={h} className="celda-hora" onClick={() => onNuevaCita(diaFecha, h)} />
                  ))}
                  {mismaFecha(diaFecha, hoy) && topHoraActual >= 0 && topHoraActual <= (HORA_FIN - HORA_INICIO) * PX_HORA && (
                    <div className="linea-hora-actual" style={{ top: topHoraActual, pointerEvents: 'none' }} />
                  )}
                  {pintarCitasDia(diaFecha)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {grupoAbierto && (
        <div className="cal-grupo-overlay" onMouseDown={e => {
          if (e.target === e.currentTarget) setGrupoAbierto(null);
        }}>
          <section className="cal-grupo-panel" role="dialog" aria-modal="true" aria-label="Citas simultáneas">
            <header className="cal-grupo-header">
              <div>
                <strong>{grupoAbierto.fecha.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
                <p>{horaTexto(grupoAbierto.inicio)} – {horaTexto(grupoAbierto.fin)} · {grupoAbierto.eventos.length} citas</p>
              </div>
              <button type="button" className="cal-grupo-cerrar" onClick={() => setGrupoAbierto(null)} aria-label="Cerrar">×</button>
            </header>
            <div className="cal-grupo-lista">
              {grupoAbierto.eventos.map(item => (
                <button key={item.cita.id} type="button" className="cal-grupo-fila"
                  style={{ borderLeftColor: colorBordeCita(item.cita) }}
                  onClick={() => { setGrupoAbierto(null); onSelectCita(item.cita); }}>
                  <span className="cal-grupo-hora">{horaTexto(item.start)} – {horaTexto(item.end)}</span>
                  <span className="cal-grupo-datos">
                    <strong className="cita-nombre"><IconoEstadoCita estado={item.cita.estadoAviso} /><span className="cita-nombre-texto">{item.cita.paciente}</span></strong>
                    <span>{item.cita.tratamiento || 'Sin tratamiento'}{item.cita.profesional ? ` · ${item.cita.profesional}` : ''}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
